/**
 * @fileoverview Countdown Timer Web Component
 *
 * A lightweight custom HTML element that displays a real-time countdown to a specified timestamp.
 * Updates every second and automatically stops when the countdown reaches zero.
 *
 * Expects Unix timestamps (seconds since epoch) as strings via the `data-timestamp` attribute.
 * The accompanying Liquid snippet handles format conversion from ISO 8601 to Unix seconds,
 * ensuring the component always receives string-based Unix timestamps.
 *
 * Supports custom HTML messages with [countdown] placeholder replacement via template elements.
 * Messages are provided by the Liquid snippet in <template> elements.
 * Respects the `data-swap-on-expire` attribute to control whether messages swap on expiration.
 *
 * @example
 * <!-- In Liquid template with Shopify metaobject -->
 * {% render 'countdown-timer',
 *   date: block.settings.countdown_date.date.value,
 *   active_message: '<p>Sale ends in <strong>[countdown]</strong></p>',
 *   expired_message: '<p>Sale has ended</p>'
 * %}
 */

import { stringToBoolean } from '@utilities/theme';

if (!customElements.get('countdown-timer')) {
  /**
   * @class CountdownTimer
   * @extends {HTMLElement}
   * @description A Web Component that displays a real-time countdown timer.
   *
   * The component:
   * - Automatically updates every second
   * - Expects Unix timestamps (seconds since epoch) as strings via data-timestamp
   * - Displays countdown in "X Days Y Hours Z Minutes" format
   * - Supports custom message templates with [countdown] placeholder
   * - Respects data-swap-on-expire attribute to control message swapping on expiration
   * - Shows custom expired message when countdown reaches zero (if swap enabled)
   * - Automatically stops updating after expiration (memory efficient)
   * - Gracefully handles invalid timestamps
   *
   * @fires No custom events are dispatched
   *
   * @example
   * const timer = document.querySelector('countdown-timer');
   * // Timer automatically updates every second
   * // Replaces [countdown] placeholder with actual time remaining
   * // No manual intervention needed
   */
  class CountdownTimer extends HTMLElement {
    /**
     * @constructor
     * @description Initializes the CountdownTimer component
     */
    constructor() {
      super();

      /**
       * @property {number|null} updateInterval - ID of the setInterval for timer updates
       * @private
       * @description Stores the interval ID for the update loop so it can be cleared on disconnect
       */
      this.updateInterval = null;

      /**
       * @property {string|null} lastCountdownString - Last countdown string that was displayed
       * @private
       * @description Tracks the last countdown string to avoid unnecessary DOM updates
       */
      this.lastCountdownString = null;

      /**
       * @property {boolean} isExpired - Whether the countdown has expired
       * @private
       * @description Tracks expiration state to avoid unnecessary DOM updates
       */
      this.isExpired = false;

      /**
       * @property {string|null} activeMessageHtml - Stored HTML content from active message template
       * @private
       * @description Stores the innerHTML from the active message template (without wrapper div)
       */
      this.activeMessageHtml = null;

      /**
       * @property {string|null} expiredMessageHtml - Stored HTML content from expired message template
       * @private
       * @description Stores the innerHTML from the expired message template (without wrapper div)
       */
      this.expiredMessageHtml = null;

      /**
       * @property {boolean} shouldSwapOnExpire - Whether to swap to expired message when countdown expires
       * @private
       * @description Controls whether the component should swap messages on expiration (set from data-swap-on-expire attribute)
       */
      this.shouldSwapOnExpire = true;
    }

    /**
     * Checks if debug mode is enabled by looking for debug class on the element
     * @returns {boolean} Whether debug logging is enabled
     */
    allowDebug() {
      return this.classList.contains("debug");
    }

    /**
     * @method connectedCallback
     * @description Lifecycle callback when element is added to the DOM
     *
     * Initializes the countdown timer:
     * - Clears any fallback/placeholder content to ensure clean display
     * - Immediately calculates and displays current countdown
     * - Sets up interval to update display every 1000ms (1 second)
     * - Continues until countdown reaches zero
     *
     * @public
     * @async
     */
    connectedCallback() {
      if (this.allowDebug()) {
        console.log('[countdown-timer] connectedCallback fired');
        console.log('[countdown-timer] Initial textContent:', JSON.stringify(this.textContent));
        console.log('[countdown-timer] Initial innerHTML:', JSON.stringify(this.innerHTML));
      }

      // Extract and store template content (innerHTML of wrapper div, not the div itself)
      const activeTemplate = this.querySelector('#countdown-timer-active-message-template');
      const expiredTemplate = this.querySelector('#countdown-timer-expired-message-template');

      if (activeTemplate?.content?.firstElementChild) {
        // Store the innerHTML of the wrapper div (the actual content)
        this.activeMessageHtml = activeTemplate.content.firstElementChild.innerHTML;
        if (this.allowDebug()) {
          console.log('[countdown-timer] Stored activeMessageHtml:', JSON.stringify(this.activeMessageHtml));
        }
      }

      if (expiredTemplate?.content?.firstElementChild) {
        // Store the innerHTML of the wrapper div (the actual content)
        this.expiredMessageHtml = expiredTemplate.content.firstElementChild.innerHTML;
        if (this.allowDebug()) {
          console.log('[countdown-timer] Stored expiredMessageHtml:', JSON.stringify(this.expiredMessageHtml));
        }
      }

      // Remove template elements after storing their content
      const templates = this.querySelectorAll('template');
      templates.forEach(template => template.remove());

      // Clear any text nodes and other elements (but keep the component clean)
      const nodesToRemove = [];
      for (const child of this.childNodes) {
        if (child.nodeType === Node.TEXT_NODE) {
          nodesToRemove.push(child);
        }
      }
      nodesToRemove.forEach(node => node.remove());

      // Read data-swap-on-expire attribute (defaults to "true" for backward compatibility)
      const swapOnExpireAttr = this.getAttribute('data-swap-on-expire');
      const parsedSwap = swapOnExpireAttr ? stringToBoolean(swapOnExpireAttr) : null;
      this.shouldSwapOnExpire = parsedSwap !== null ? parsedSwap : true;

      if (this.allowDebug()) {
        console.log('[countdown-timer] Templates removed, content stored');
        console.log('[countdown-timer] Active message:', JSON.stringify(this.activeMessageHtml));
        console.log('[countdown-timer] Expired message:', JSON.stringify(this.expiredMessageHtml));
        console.log('[countdown-timer] shouldSwapOnExpire:', this.shouldSwapOnExpire);
      }

      this.updateTime();
      this.updateInterval = setInterval(() => {
        this.updateTime();
      }, 1000);
      // Update every second (1000ms)
    }

    /**
     * @method disconnectedCallback
     * @description Lifecycle callback when element is removed from the DOM
     *
     * Cleans up resources:
     * - Clears the update interval to prevent memory leaks
     * - Stops any ongoing countdown operations
     *
     * @public
     */
    disconnectedCallback() {
      clearInterval(this.updateInterval);
    }

    /**
     * @method parseTimestamp
     * @description Parses a Unix timestamp string to a number
     *
     * Converts numeric strings to integers. The Liquid snippet handles
     * ISO 8601 to Unix timestamp conversion before passing to the component,
     * so this method only expects string-based Unix timestamps.
     *
     * @param {string} value - The timestamp value as a string (Unix seconds)
     * @returns {number|null} Unix timestamp in seconds, or null if invalid
     *
     * @example
     * const timer = document.querySelector('countdown-timer');
     * timer.parseTimestamp('1735689600');  // Returns: 1735689600
     *
     * @public
     */
    parseTimestamp(value) {
      const stringValue = String(value).trim();

      // Parse numeric string as Unix timestamp
      if (/^\d+$/.test(stringValue)) {
        const parsed = parseInt(stringValue, 10);
        if (this.allowDebug()) {
          console.log('[countdown-timer] parseTimestamp:', stringValue, '→', parsed);
        }
        return parsed;
      }

      // Invalid format
      if (this.allowDebug()) {
        console.log('[countdown-timer] parseTimestamp FAILED:', value);
      }
      return null;
    }


    /**
     * @method injectHtmlContent
     * @description Injects HTML content directly into the element without wrapper divs
     * @param {string} html - HTML string to inject
     * @private
     */
    injectHtmlContent(html) {
      // Create a temporary container to parse the HTML
      const temp = document.createElement('div');
      temp.innerHTML = html;

      // Extract all child nodes (without the wrapper div)
      const nodes = Array.from(temp.childNodes);

      // Replace all children with the parsed nodes
      this.replaceChildren(...nodes);
    }

    /**
     * @method updateTime
     * @description Calculates remaining time and updates the element's content
     *
     * Process:
     * 1. Retrieves and validates Unix timestamp string from `data-timestamp` attribute
     * 2. Exits early if timestamp is missing or invalid
     * 3. Calculates remaining time from now
     * 4. Builds countdown string in "X Days Y Hours Z Minutes" format
     * 5. Only updates DOM if countdown string has changed
     * 6. Uses stored template HTML and replaces [countdown] placeholder
     * 7. On expiration: swaps to expired message if data-swap-on-expire="true", otherwise keeps active message
     * 8. Clears interval after expiration or error
     *
     * Message behavior:
     * - Uses stored HTML from templates (without wrapper div)
     * - Injects HTML directly into countdown-timer element (inline compatible)
     * - Replaces [countdown] placeholder in the HTML string
     * - Only updates DOM when countdown string actually changes
     * - Falls back to text content if templates are not available
     *
     * Error handling (exits immediately):
     * - Missing `data-timestamp` attribute: displays "Invalid timestamp"
     * - Invalid timestamp format: displays "Invalid timestamp"
     *
     * @private
     *
     * @example
     * // Called automatically by connectedCallback
     * // Called automatically every 1000ms by setInterval
     * // Can be called manually if needed:
     * const timer = document.querySelector('countdown-timer');
     * timer.updateTime();
     */
    updateTime() {
      const timestampValue = this.getAttribute('data-timestamp');

      // Exit early if no valid timestamp provided
      if (!timestampValue) {
        if (this.allowDebug()) {
          console.log('[countdown-timer] updateTime: NO TIMESTAMP');
        }
        clearInterval(this.updateInterval);
        // Use stored active message or fallback to text
        if (this.activeMessageHtml) {
          this.injectHtmlContent('Invalid timestamp');
        } else {
          this.textContent = 'Invalid timestamp';
        }
        return;
      }

      // Parse the timestamp
      const timestamp = this.parseTimestamp(timestampValue);

      // Exit early if timestamp parsing failed
      if (timestamp === null) {
        if (this.allowDebug()) {
          console.log('[countdown-timer] updateTime: PARSE FAILED');
        }
        clearInterval(this.updateInterval);
        // Use stored active message or fallback to text
        if (this.activeMessageHtml) {
          this.injectHtmlContent('Invalid timestamp');
        } else {
          this.textContent = 'Invalid timestamp';
        }
        return;
      }

      // Calculate remaining time
      const now = Math.floor(Date.now() / 1000);
      const remainingSeconds = timestamp - now;

      // Get message content from stored HTML (templates are always provided by Liquid snippet)
      const activeMessage = this.activeMessageHtml || '[countdown]';
      const expiredMessage = this.expiredMessageHtml || activeMessage;

      if (this.allowDebug()) {
        console.log('[countdown-timer] updateTime: activeMessage =', JSON.stringify(activeMessage), 'expiredMessage =', JSON.stringify(expiredMessage));
      }

      // Handle countdown expiration
      if (remainingSeconds <= 0) {
        // Only update if not already expired
        if (!this.isExpired) {
          if (this.allowDebug()) {
            console.log('[countdown-timer] updateTime: COUNTDOWN EXPIRED');
            console.log('[countdown-timer] updateTime: shouldSwapOnExpire =', this.shouldSwapOnExpire);
          }

          // Determine which message to display based on shouldSwapOnExpire flag
          let displayHtml;
          if (this.shouldSwapOnExpire) {
            // Swap to expired message
            displayHtml = expiredMessage.includes('[countdown]')
              ? expiredMessage.replace('[countdown]', '0 Minutes')
              : expiredMessage;

            if (this.allowDebug()) {
              console.log('[countdown-timer] updateTime: Using expired message:', JSON.stringify(displayHtml));
            }
          } else {
            // Keep showing active message with "0 Minutes" replacement
            displayHtml = activeMessage.includes('[countdown]')
              ? activeMessage.replace('[countdown]', '0 Minutes')
              : activeMessage;

            if (this.allowDebug()) {
              console.log('[countdown-timer] updateTime: Keeping active message:', JSON.stringify(displayHtml));
            }
          }

          // Inject HTML directly (without wrapper div) if we have stored HTML, otherwise use text
          if (this.activeMessageHtml || this.expiredMessageHtml) {
            this.injectHtmlContent(displayHtml);
          } else {
            this.textContent = displayHtml;
          }

          this.isExpired = true;
        }
        clearInterval(this.updateInterval);
        return;
      }

      // Reset expired flag if countdown is active again
      this.isExpired = false;

      // Calculate days, hours, and minutes
      const days = Math.floor(remainingSeconds / 86400);
      const hours = Math.floor((remainingSeconds % 86400) / 3600);
      const minutes = Math.floor(((remainingSeconds % 86400) % 3600) / 60);

      // Build countdown string (only showing non-zero values)
      let countdownString = '';
      if (days > 0) countdownString += `${days} ${days === 1 ? 'Day' : 'Days'}`;
      if (hours > 0) countdownString += ` ${hours} ${hours === 1 ? 'Hour' : 'Hours'}`;
      if (minutes > 0) countdownString += ` ${minutes} ${minutes === 1 ? 'Minute' : 'Minutes'}`;
      countdownString = countdownString.trim() || '0 Minute';

      // Only update if countdown string has changed
      if (countdownString === this.lastCountdownString) {
        if (this.allowDebug()) {
          console.log('[countdown-timer] updateTime: Countdown unchanged, skipping update');
        }
        return;
      }

      this.lastCountdownString = countdownString;

      // Replace [countdown] placeholder in active message HTML
      let displayHtml = activeMessage;
      if (activeMessage.includes('[countdown]')) {
        displayHtml = activeMessage.replace('[countdown]', countdownString);
      } else if (!this.activeMessageHtml) {
        // If no stored HTML and no placeholder, use countdown string directly
        displayHtml = countdownString;
      }

      if (this.allowDebug()) {
        console.log('[countdown-timer] updateTime: countdownString =', JSON.stringify(countdownString));
        console.log('[countdown-timer] updateTime: displayHtml =', JSON.stringify(displayHtml));
      }

      // Inject HTML directly (without wrapper div) if we have stored HTML, otherwise use text
      if (this.activeMessageHtml) {
        this.injectHtmlContent(displayHtml);
      } else {
        this.textContent = displayHtml;
      }
    }
  }

  // Register the custom element
  customElements.define('countdown-timer', CountdownTimer);
}

